'use client'

import { useState, useEffect, useRef } from 'react'
import { TextField } from '~/app/_components/Fields'
import { Button } from '~/app/_components/Button'
import AddressAutocomplete from '~/app/_features/app/profile/AddressAutocomplete'
import BackgroundMap from '~/components/BackgroundMap'
import { api } from '~/trpc/react'
import { useDebounce } from '~/lib/utils/useDebounce'
import { HeartIcon, BriefcaseIcon } from '@heroicons/react/24/outline'
import { Avatar } from '~/components/DefaultAvatar'
import { auth } from '~/components/FirebaseProvider'
import { uploadFileToFirebase } from '~/lib/firebase-storage'

interface BasicInfoStepProps {
  firebaseUid: string
  authPhotoURL: string | null
  initialData?: {
    firstName?: string
    lastName?: string
    email?: string
    phoneNumber?: string
    address?: string
    zipCode?: string
    lat?: number
    long?: number
  }
  onComplete: (data: {
    role: 'CAREGIVER' | 'CLIENT'
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    address: string
    zipCode: string
    lat: number
    long: number
    serviceIds: string[]
    workingRadius?: number | null
  }) => void
}

type SubStep = 'role' | 'basic' | 'address' | 'services'

// Sub-steps after role selection (shown in stepper)
const SUB_STEP_ORDER: SubStep[] = ['basic', 'address', 'services']

function TooltipWrapper({ 
  children, 
  message, 
  isDisabled,
  buttonId,
  hoveredButton,
  setHoveredButton
}: { 
  children: React.ReactNode
  message: string | null
  isDisabled: boolean
  buttonId: string
  hoveredButton: string | null
  setHoveredButton: (id: string | null) => void
}) {
  const showTooltip = isDisabled && message && hoveredButton === buttonId

  return (
    <div 
      className="relative flex-1"
      onMouseEnter={() => isDisabled && message && setHoveredButton(buttonId)}
      onMouseLeave={() => setHoveredButton(null)}
    >
      {children}
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-md z-50 shadow-lg pointer-events-none max-w-xs">
          {message}
          <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  )
}

function SubStepStepper({ 
  currentStep, 
  onStepClick 
}: { 
  currentStep: SubStep
  onStepClick: (step: SubStep) => void 
}) {
  const currentIndex = SUB_STEP_ORDER.indexOf(currentStep)
  
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {SUB_STEP_ORDER.map((step, index) => {
        const isPast = index < currentIndex
        const isCurrent = index === currentIndex
        const isClickable = isPast || isCurrent
        
        return (
          <div key={step} className="flex items-center">
            <button
              type="button"
              onClick={() => isClickable && onStepClick(step)}
              disabled={!isClickable}
              className={`
                flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors
                ${isCurrent 
                  ? 'bg-primary text-white' 
                  : isPast 
                    ? 'bg-gray-300 text-gray-700 hover:bg-gray-400 cursor-pointer' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {index + 1}
            </button>
            {index < SUB_STEP_ORDER.length - 1 && (
              <div 
                className={`w-8 h-0.5 mx-1 ${
                  isPast || isCurrent ? 'bg-gray-300' : 'bg-gray-200'
                }`} 
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

export function BasicInfoStep({ firebaseUid, authPhotoURL, initialData, onComplete }: BasicInfoStepProps) {
  const [subStep, setSubStep] = useState<SubStep>('role')
  const [role, setRole] = useState<'CAREGIVER' | 'CLIENT' | null>(null)
  const [firstName, setFirstName] = useState(initialData?.firstName ?? '')
  const [lastName, setLastName] = useState(initialData?.lastName ?? '')
  const [email, setEmail] = useState(initialData?.email ?? '')
  const [phoneNumber, setPhoneNumber] = useState(initialData?.phoneNumber ?? '')
  const [address, setAddress] = useState(initialData?.address ?? '')
  const [zipCode, setZipCode] = useState(initialData?.zipCode ?? '')
  const [lat, setLat] = useState(initialData?.lat ?? 0)
  const [long, setLong] = useState(initialData?.long ?? 0)
  const [selectedServices, setSelectedServices] = useState<string[]>([])
  const [hoveredButton, setHoveredButton] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string>('daily')
  const [currentPage, setCurrentPage] = useState<Record<string, number>>({})
  const [workingRadius, setWorkingRadius] = useState<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarOverrideUrl, setAvatarOverrideUrl] = useState<string | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarSyncLoading, setAvatarSyncLoading] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const utils = api.useUtils()
  const syncJoinAvatarMutation = api.post.syncJoinAvatar.useMutation()

  const displayAvatarUrl = avatarOverrideUrl ?? authPhotoURL ?? null

  useEffect(() => {
    setAvatarOverrideUrl(null)
  }, [authPhotoURL])

  // Update fields when initialData changes
  useEffect(() => {
    if (initialData) {
      if (initialData.firstName) setFirstName(initialData.firstName)
      if (initialData.lastName) setLastName(initialData.lastName)
      if (initialData.email) setEmail(initialData.email)
      if (initialData.phoneNumber) setPhoneNumber(initialData.phoneNumber)
      if (initialData.address) setAddress(initialData.address)
      if (initialData.zipCode) setZipCode(initialData.zipCode)
      if (initialData.lat) setLat(initialData.lat)
      if (initialData.long) setLong(initialData.long)
    }
  }, [initialData])

  // Set default working radius when role is CAREGIVER
  useEffect(() => {
    if (role === 'CAREGIVER' && workingRadius === null) {
      setWorkingRadius(15)
    } else if (role === 'CLIENT') {
      setWorkingRadius(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role])

  // Reset page when tab changes (only in services sub-step)
  useEffect(() => {
    if (subStep === 'services') {
      setCurrentPage(prev => {
        if (prev[activeTab] === undefined) {
          return { ...prev, [activeTab]: 0 }
        }
        return prev
      })
    }
  }, [subStep, activeTab])

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint in Tailwind
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Check email existence with debounce (for internal housekeeping - no UI feedback)
  const debouncedEmail = useDebounce(email, 500)
  api.post.checkEmailExists.useQuery(
    { email: debouncedEmail },
    { 
      enabled: !!debouncedEmail && debouncedEmail.includes('@') && subStep === 'basic',
      retry: false,
    }
  )

  const { data: services, isLoading: isLoadingServices, error: servicesError } = api.post.getServices.useQuery()


  const handleAddressChange = (newAddress: string, latitude: number, longitude: number, newZipCode: string) => {
    setAddress(newAddress)
    setLat(latitude)
    setLong(longitude)
    setZipCode(newZipCode)
  }

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '')
    
    // Limit to 10 digits (US phone number)
    const phoneNumberLength = phoneNumber.length
    if (phoneNumberLength === 0) return ''
    if (phoneNumberLength < 4) return phoneNumber
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
  }

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhoneNumber(formatted)
  }

  const getBasicInfoTooltip = () => {
    const missing: string[] = []
    if (!firstName?.trim()) missing.push('First Name')
    if (!lastName?.trim()) missing.push('Last Name')
    if (!email?.trim()) missing.push('Email')
    if (!phoneNumber?.trim()) missing.push('Phone Number')
    
    if (missing.length === 0) return null
    return `Please fill in: ${missing.join(', ')}`
  }

  const getAddressTooltip = () => {
    if (!address || lat === 0 || long === 0) {
      return 'Please enter and select a valid address'
    }
    return null
  }


  const toggleService = (serviceId: string) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    )
  }

  const handleFinalSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!role || !firstName || !lastName || !email || !phoneNumber || !address) {
      return
    }

    onComplete({
      role,
      firstName,
      lastName,
      email,
      phoneNumber,
      address,
      zipCode: zipCode || '',
      lat,
      long,
      serviceIds: selectedServices,
      workingRadius: role === 'CAREGIVER' ? workingRadius : null,
    })
  }

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const user = auth.currentUser
    if (!user) {
      setAvatarError('You must be signed in to upload a photo.')
      return
    }
    setAvatarError(null)
    setAvatarUploading(true)
    try {
      const timestamp = Date.now()
      const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`
      const path = `providers/${user.uid}/photos/${fileName}`
      const url = await uploadFileToFirebase(file, path)
      setAvatarOverrideUrl(url)
    } catch (err) {
      console.error('Join avatar upload:', err)
      setAvatarError(err instanceof Error ? err.message : 'Failed to upload photo')
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleBasicContinueToAddress = async () => {
    const canProceed =
      firstName?.trim() &&
      lastName?.trim() &&
      email?.trim() &&
      phoneNumber?.trim()
    if (!canProceed) return
    setAvatarError(null)
    setAvatarSyncLoading(true)
    try {
      await syncJoinAvatarMutation.mutateAsync({
        firebaseUid,
        storageUrl: avatarOverrideUrl ?? undefined,
      })
      await utils.post.getUserByFirebaseUid.invalidate({ firebaseUid })
      setSubStep('address')
    } catch (err) {
      console.error('syncJoinAvatar:', err)
      setAvatarError(err instanceof Error ? err.message : 'Could not save profile photo')
    } finally {
      setAvatarSyncLoading(false)
    }
  }

  // Render Role Selection Sub-step
  if (subStep === 'role') {
    return (
      <div className="space-y-4">
        <div className="text-center mb-2">
          <p className="text-gray-700 font-medium">
            How can Tigo help you?
          </p>
        </div>
        <div className="space-y-3">
        <button
          type="button"
          onClick={() => {
            setRole('CLIENT')
            setSubStep('basic')
          }}
          className="w-full p-4 border-2 rounded-lg text-left transition-colors border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-primary flex items-center gap-4 cursor-pointer"
        >
          <div className="shrink-0 flex items-center justify-center">
            <HeartIcon className="w-8 h-8 text-primary" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-gray-900">Seeking Care</div>
            <div className="text-sm text-gray-600 mt-1">I need care for myself or a loved one</div>
          </div>
        </button>
        
        <button
          type="button"
          onClick={() => {
            setRole('CAREGIVER')
            setSubStep('basic')
          }}
          className="w-full p-4 border-2 rounded-lg text-left transition-colors border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-primary flex items-center gap-4 cursor-pointer"
        >
          <div className="shrink-0 flex items-center justify-center">
            <BriefcaseIcon className="w-8 h-8 text-primary" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-gray-900">Provide Care</div>
            <div className="text-sm text-gray-600 mt-1">I&apos;m a care professional looking for opportunities</div>
          </div>
        </button>
        </div>
      </div>
    )
  }

  // Render Basic Info Sub-step
  if (subStep === 'basic') {
    const canContinue = 
      firstName?.trim() && 
      lastName?.trim() && 
      email?.trim() && 
      phoneNumber?.trim()

    return (
      <div className="space-y-6">
        <SubStepStepper 
          currentStep={subStep} 
          onStepClick={(step) => setSubStep(step)} 
        />
        
        <div className="text-center mb-4">
          <p className="text-gray-700 font-medium mb-1">
            Tell us about yourself
          </p>
          <p className="text-sm text-gray-500">
            We need some basic information to get started
          </p>
        </div>

        <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-5 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <Avatar
            src={displayAvatarUrl}
            alt="Your profile"
            size={88}
            className="ring-2 ring-white shadow-sm shrink-0"
          />
          <div className="flex-1 text-center sm:text-left min-w-0 space-y-2">
            <p className="text-sm text-gray-600">
              {displayAvatarUrl
                ? 'We’ll show this photo on your profile. Change it anytime, or skip as-is.'
                : 'Optional: add a photo so people recognize you—you can skip and add one later.'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarFileChange}
            />
            <Button
              type="button"
              variant="outline"
              className="text-sm py-1.5 px-3"
              disabled={avatarUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {avatarUploading ? 'Uploading…' : displayAvatarUrl ? 'Use a different photo' : 'Add a photo'}
            </Button>
          </div>
        </div>

        {avatarError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800 text-center">
            {avatarError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>

        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <TextField
          label="Phone Number"
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          placeholder="(555) 123-4567"
          required
        />

        <div className="flex gap-3">
          <Button
            type="button"
            onClick={() => setSubStep('role')}
            variant="outline"
            className="flex-1"
          >
            Back
          </Button>
          <TooltipWrapper
            message={getBasicInfoTooltip()}
            isDisabled={!canContinue || avatarSyncLoading}
            buttonId="basic-continue"
            hoveredButton={hoveredButton}
            setHoveredButton={setHoveredButton}
          >
            <Button
              type="button"
              onClick={() => void handleBasicContinueToAddress()}
              disabled={!canContinue || avatarSyncLoading}
              className="w-full"
              color="primary"
            >
              {avatarSyncLoading ? 'Continuing…' : 'Continue'}
            </Button>
          </TooltipWrapper>
        </div>
      </div>
    )
  }

  // Render Address Sub-step
  if (subStep === 'address') {
    const canContinue = address && lat !== 0 && long !== 0

    return (
      <div className="space-y-6">
        <SubStepStepper 
          currentStep={subStep} 
          onStepClick={(step) => setSubStep(step)} 
        />
        
        <div className="text-center mb-4">
          <p className="text-gray-700 font-medium mb-1">
            {role === 'CAREGIVER' ? 'Where are you located?' : 'Where will care be provided?'}
          </p>
          <p className="text-sm text-gray-500">
            {role === 'CAREGIVER' 
              ? "Enter your home address. We'll use this location to define your service area."
              : role === 'CLIENT'
              ? "Enter the address where care will be provided. We'll use this to connect you with nearby care professionals."
              : 'Enter an address to continue.'}
          </p>
        </div>

        <div className="relative z-10">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {role === 'CAREGIVER' ? 'Home Address *' : 'Address *'}
          </label>
          <AddressAutocomplete
            value={address}
            onChange={handleAddressChange}
            placeholder={role === 'CAREGIVER' ? 'Enter your home address' : 'Enter the address where care will be provided'}
          />
        </div>

        {/* Working Radius for Caregivers */}
        {role === 'CAREGIVER' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Working Radius (miles)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={workingRadius ?? 15}
                onChange={(e) => {
                  try {
                    e.stopPropagation()
                    const value = parseInt(e.target.value, 10)
                    if (!isNaN(value) && value >= 5 && value <= 100) {
                      setWorkingRadius(value)
                    }
                  } catch (error) {
                    console.error('Error updating working radius:', error)
                  }
                }}
                className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-primary slider"
              />
              <span className="min-w-12 text-sm font-medium text-gray-900 text-right">
                {workingRadius ?? 15} mi
              </span>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>5 mi</span>
              <span>Typical: 10-25 miles</span>
              <span>100 mi</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">This is how far you&apos;re willing to travel for work.</p>
          </div>
        )}

        {/* Map display when address is selected */}
        {lat !== 0 && long !== 0 && (
          <div className="relative z-0 rounded-lg overflow-hidden border border-gray-200">
            <div className="h-64">
              <BackgroundMap
                latitude={lat}
                longitude={long}
                radiusMiles={role === 'CAREGIVER' ? (workingRadius ?? undefined) : undefined}
                showPin={true}
                zoom={role === 'CAREGIVER' && workingRadius ? undefined : 14}
                size="full"
                showFade={false}
                className="h-full"
              />
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            onClick={() => setSubStep('basic')}
            variant="outline"
            className="flex-1"
          >
            Back
          </Button>
          <TooltipWrapper
            message={getAddressTooltip()}
            isDisabled={!canContinue}
            buttonId="address-continue"
            hoveredButton={hoveredButton}
            setHoveredButton={setHoveredButton}
          >
            <Button
              type="button"
              onClick={() => setSubStep('services')}
              disabled={!canContinue}
              className="w-full"
              color="primary"
            >
              Continue
            </Button>
          </TooltipWrapper>
        </div>
      </div>
    )
  }

  // Render Services Sub-step (final sub-step)
  if (subStep === 'services') {
    
    // Group services by category (no "Most Common" tab - all services are grouped)
    type ServiceGroup = {
      name: string
      keywords: string[]
      services: NonNullable<typeof services>
    }
    
    const serviceGroups: Record<string, ServiceGroup> = {
      daily: {
        name: 'Daily Living',
        keywords: [
          'personal care', 'companionship', 'meal', 'housekeeping', 'nutrition', 
          'activity planning', 'social engagement', 'light housekeeping',
          'mental health', 'behavioral', 'grief support'
        ],
        services: []
      },
      medical: {
        name: 'Medical Care',
        keywords: [
          'medication', 'wound', 'first aid', 'emergency', 'respiratory', 'oxygen',
          'dementia', 'memory care', 'rehabilitation support', 'adaptive equipment', 'mobility assistance',
          'hospice', 'palliative', 'end of life', 'end-of-life'
        ],
        services: []
      },
      therapy: {
        name: 'Therapy',
        keywords: ['physical therapy', 'occupational therapy', 'mobility therapy', 'therapeutic massage'],
        services: []
      },
      other: {
        name: 'Other Services',
        keywords: [],
        services: []
      }
    }
    
    // Group all services into categories
    const allServices = services ?? []
    allServices.forEach(service => {
      const serviceNameLower = service.name.toLowerCase()
      const serviceDescLower = service.description.toLowerCase()
      let categorized = false
      
      for (const [key, group] of Object.entries(serviceGroups)) {
        if (key === 'other') continue
        
        if (group.keywords.some(keyword => 
          serviceNameLower.includes(keyword) || serviceDescLower.includes(keyword)
        )) {
          group.services.push(service)
          categorized = true
          break
        }
      }
      
      if (!categorized && serviceGroups.other) {
        serviceGroups.other.services.push(service)
      }
    })
    
    // Get active tab services
    const activeTabGroup = serviceGroups[activeTab]
    const activeTabServices = activeTabGroup?.services ?? []
    
    // Find first tab with services for initial display (prefer 'daily')
    const firstTabWithServices = Object.entries(serviceGroups).find(([_, group]) => group.services.length > 0)?.[0] ?? 'daily'
    
    // Use first tab with services if current tab is empty
    const displayTab = activeTabServices.length > 0 ? activeTab : firstTabWithServices
    const displayTabServices = serviceGroups[displayTab]?.services ?? []
    
    // Pagination settings: 
    // Mobile: 1 column, 4 rows = 4 services per page
    // Desktop: 2 columns, 3 rows = 6 services per page
    const servicesPerPage = isMobile ? 4 : 6
    const currentPageIndex = currentPage[displayTab] ?? 0
    const totalPages = Math.ceil(displayTabServices.length / servicesPerPage)
    const startIndex = currentPageIndex * servicesPerPage
    const endIndex = startIndex + servicesPerPage
    const currentPageServices = displayTabServices.slice(startIndex, endIndex)
    
    // Split services into columns
    // Mobile: single column (all services)
    // Desktop: 2 columns (alternating)
    const servicesColumn1 = isMobile 
      ? currentPageServices 
      : currentPageServices.filter((_, index) => index % 2 === 0)
    const servicesColumn2 = isMobile 
      ? [] 
      : currentPageServices.filter((_, index) => index % 2 === 1)
    
    const handlePreviousPage = () => {
      if (currentPageIndex > 0) {
        setCurrentPage(prev => ({ ...prev, [displayTab]: currentPageIndex - 1 }))
      }
    }
    
    const handleNextPage = () => {
      if (currentPageIndex < totalPages - 1) {
        setCurrentPage(prev => ({ ...prev, [displayTab]: currentPageIndex + 1 }))
      }
    }

    return (
      <form onSubmit={handleFinalSubmit} className="space-y-6">
        <SubStepStepper 
          currentStep={subStep} 
          onStepClick={(step) => setSubStep(step)} 
        />
        
        <div className="text-center mb-4">
          <p className="text-gray-700 font-medium mb-1">
            {role === 'CAREGIVER' ? 'What services do you provide?' : 'What services do you need?'}
          </p>
          <p className="text-sm text-gray-500">
            Select all that apply (optional)
          </p>
        </div>

        {isLoadingServices ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          </div>
        ) : servicesError ? (
          <div className="text-center py-8">
            <p className="text-red-600 mb-2">Failed to load services</p>
            <p className="text-sm text-gray-500">{servicesError.message || 'Please try again later'}</p>
          </div>
        ) : services && services.length > 0 ? (
          <>
            {/* Services by Category with Tabs */}
            {services.length > 0 && (
              <div>
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="-mb-px flex flex-wrap gap-x-4 gap-y-2" aria-label="Tabs">
                    {Object.entries(serviceGroups).map(([key, group]) => {
                      if (group.services.length === 0) return null
                      const isActive = activeTab === key
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setActiveTab(key)}
                          className={`
                            whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm
                            ${isActive
                              ? 'border-sage text-sage'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
                          `}
                        >
                          {group.name}
                          {group.services.length > 0 && (
                            <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                              isActive ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {group.services.length}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </nav>
                </div>
                
                {/* Services Carousel for Active Tab */}
                {displayTabServices.length > 0 ? (
                  <div className="relative">
                    {/* Navigation Arrows */}
                    {totalPages > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={handlePreviousPage}
                          disabled={currentPageIndex === 0}
                          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-lg border border-gray-200 ${
                            currentPageIndex === 0
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-gray-50 cursor-pointer'
                          }`}
                        >
                          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={handleNextPage}
                          disabled={currentPageIndex >= totalPages - 1}
                          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-lg border border-gray-200 ${
                            currentPageIndex >= totalPages - 1
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-gray-50 cursor-pointer'
                          }`}
                        >
                          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                    
                    {/* Services Grid - Mobile: 1 column, 4 rows | Desktop: 2 columns, 3 rows */}
                    <div className="px-4 md:px-10">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Column 1 */}
                        <div className="space-y-3">
                          {servicesColumn1.map((service) => (
                            <button
                              key={service.id}
                              type="button"
                              onClick={() => toggleService(service.id)}
                              className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                                selectedServices.includes(service.id)
                                  ? 'border-primary bg-primary/10'
                                  : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 mb-1">{service.name}</div>
                                  <div className="text-xs text-gray-600 line-clamp-2">{service.description}</div>
                                </div>
                                <div className={`ml-2 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                                  selectedServices.includes(service.id)
                                    ? 'border-primary bg-primary'
                                    : 'border-gray-300'
                                }`}>
                                  {selectedServices.includes(service.id) && (
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                        
                        {/* Column 2 - Desktop only */}
                        {!isMobile && (
                          <div className="space-y-3">
                            {servicesColumn2.map((service) => (
                              <button
                                key={service.id}
                                type="button"
                                onClick={() => toggleService(service.id)}
                                className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                                  selectedServices.includes(service.id)
                                    ? 'border-primary bg-primary/10'
                                    : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 mb-1">{service.name}</div>
                                    <div className="text-xs text-gray-600 line-clamp-2">{service.description}</div>
                                  </div>
                                  <div className={`ml-2 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                                    selectedServices.includes(service.id)
                                      ? 'border-primary bg-primary'
                                      : 'border-gray-300'
                                  }`}>
                                    {selectedServices.includes(service.id) && (
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Page Indicator */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4">
                          <span className="text-xs text-gray-500">
                            Page {currentPageIndex + 1} of {totalPages}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-4">No services in this category</p>
                )}
                
                {/* Selected services display */}
                {selectedServices.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-600 mb-2">Selected services:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedServices.map(serviceId => {
                        const service = services?.find(s => s.id === serviceId)
                        if (!service) return null
                        return (
                          <span
                            key={serviceId}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary-dark rounded-full text-sm"
                          >
                            {service.name}
                            <button
                              type="button"
                              onClick={() => toggleService(serviceId)}
                              className="text-primary hover:text-primary-dark"
                            >
                              ×
                            </button>
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No services available</p>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            onClick={() => setSubStep('address')}
            variant="outline"
            className="flex-1"
          >
            Back
          </Button>
          <Button
            type="submit"
            className="w-full"
            color="primary"
          >
            Complete Registration
          </Button>
        </div>
      </form>
    )
  }

  return null
}
